import {useInfiniteQuery, UseInfiniteQueryResult, useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import i18n from 'i18next';
import {fetchConversations, getConversation, markConversationAsSeen, deleteConversation} from '../../../service/message';
import {fetchConnections} from '../../../service/connection.service';
import {IPaginatedResult} from '../../../incase-domain/search';
import {IConversation} from '../../../incase-domain/message';
import {IConnection} from '../../../incase-domain/connection';
import {IUpdateError} from '../../../store/actions/types/error.types';
import useDebounceSearchTerm from '../../../hooks/useDebounceSearchTerm';
import {QUERY_KEYS} from '../../../utils/queryKeys';

interface UseConversationsOptions {
    searchString: string;
    searchBy?: string;
    pageSize?: number;
    refetchInterval?: number;
}

interface UseConnectionsOptions {
    searchString: string;
    pageSize?: number;
}

interface UseStartConversationOptions {
    loggedInUserId: string;
    loggedInUserEmail: string;
    onSuccess?: (conversation: IConversation) => void;
    onError?: () => void;
}

interface UseDeleteConversationOptions {
    allConversations: IConversation[];
    onSuccess?: () => void;
    onError?: () => void;
}

interface UseDeleteConversationReturn {
    deleteConversationMutation: UseMutationResult<{success: boolean}, IUpdateError, string>;
    handleDeleteConversationAction: (conversationId: string) => void;
}

export const useConversations = ({
    searchString,
    searchBy = 'email',
    pageSize = 20,
    refetchInterval = 5000,
}: UseConversationsOptions): UseInfiniteQueryResult<IPaginatedResult<IConversation>, Error> => {
    const debouncedSearchTerm = useDebounceSearchTerm(searchString, 500);

    return useInfiniteQuery(
        QUERY_KEYS.conversation.list({search: debouncedSearchTerm}),
        ({pageParam = 1}) => fetchConversations({searchString: debouncedSearchTerm, searchBy, pageSize, pageNumber: pageParam}),
        {
            staleTime: 0,
            cacheTime: 0,
            getNextPageParam: (data) => {
                return data.pageNumber < data.numberOfElements / data.pageSize ? data.pageNumber + 1 : undefined;
            },
            refetchInterval,
        }
    );
};

export const useConnections = ({
    searchString,
    pageSize = 20,
}: UseConnectionsOptions): UseInfiniteQueryResult<IPaginatedResult<IConnection>, Error> => {
    const debouncedSearchTerm = useDebounceSearchTerm(searchString, 500);

    return useInfiniteQuery(
        QUERY_KEYS.messageConnection.list({search: debouncedSearchTerm}),
        ({pageParam = 1}) => fetchConnections({searchString: debouncedSearchTerm, pageSize, pageNumber: pageParam}),
        {
            staleTime: 0,
            cacheTime: 0,
            getNextPageParam: (data) => {
                return data.pageNumber < data.numberOfElements / data.pageSize ? data.pageNumber + 1 : undefined;
            },
        }
    );
};

export const useStartConversation = ({
    loggedInUserId,
    loggedInUserEmail,
    onSuccess,
    onError,
}: UseStartConversationOptions): UseMutationResult<IConversation, IUpdateError, IConnection> => {
    const client = useQueryClient();
    const navigate = useNavigate();
    const {t} = useTranslation();

    return useMutation(
        (connection: IConnection) =>
            getConversation([
                {userId: connection.userId, email: connection.email},
                {userId: loggedInUserId, email: loggedInUserEmail},
            ]),
        {
            onSuccess: (conversation) => {
                client.invalidateQueries({queryKey: QUERY_KEYS.messageConnection.lists()});
                client.invalidateQueries({queryKey: QUERY_KEYS.conversation.lists()});
                navigate(`/messages/${conversation._id}`);
                onSuccess?.(conversation);
            },
            onError: () => {
                toast.error(t('toasts.errorOccurred'));
                onError?.();
            },
        }
    );
};

export const useDeleteConversation = ({
    allConversations,
    onSuccess,
    onError,
}: UseDeleteConversationOptions): UseDeleteConversationReturn => {
    const client = useQueryClient();
    const navigate = useNavigate();

    const handleDeleteConversationAction = (conversationId: string): void => {
        client.invalidateQueries({queryKey: QUERY_KEYS.conversation.lists()}).then(() => {
            toast.success(i18n.t('messaging.messageDeleted'));
            const filteredConversations = allConversations.filter((conversation: IConversation) => conversation._id !== conversationId);
            if (filteredConversations.length > 0) {
                navigate(`/messages/${filteredConversations[0]._id}`);
            }
            onSuccess?.();
        });
    };

    const deleteConversationMutation: UseMutationResult<{success: boolean}, IUpdateError, string> = useMutation(
        (conversationId: string) => deleteConversation(conversationId),
        {
            onSuccess: (result, conversationId) => {
                if (result.success) {
                    handleDeleteConversationAction(conversationId);
                }
            },
            onError: () => {
                onError?.();
            },
        }
    );

    return {
        deleteConversationMutation,
        handleDeleteConversationAction,
    };
};

export const useMarkConversationAsSeen = (): UseMutationResult<void, IUpdateError, string> => {
    return useMutation((conversationId: string) => markConversationAsSeen(conversationId));
};
